<template>
  <div class="plan-card" :class="`is-${type}`">
    <div class="plan-card__inner">
      <div class="plan-card__price-wrapper">
        <div class="plan-card__price">
          <sup v-if="planOldPrice" class="plan-card__price-old">{{ getFormattedMoney(planOldPrice) }}</sup>
          <sup class="plan-card__price-sup">$</sup>
          <span class="plan-card__price-dollars" v-html="getFormattedDollars(planPrice)" />
          <span class="plan-card__price-cents" v-html="getFormattedCents(planPrice)" />
          <span class="plan-card__price-cents">/m</span>
          <div v-if="hasPromoPrice" class="plan-card__details is-text">
            <slot name="detailsText" :offer="offerHalfPrice">6 months half price</slot>
          </div>
          <div v-else-if="(hasTerm && planOldPrice) || freeDiscount" class="plan-card__details is-text">
            <slot name="detailsText" :offer="offerHalfPrice">For the first {{ offerHalfPrice }} months</slot>
          </div>
          <div v-else-if="hasNoTermDiscount" class="plan-card__details is-text">
            <slot name="detailsText" :offer="offerHalfPrice">15% discount for Student Card</slot>
          </div>
        </div>
      </div>
      <h4 class="plan-card__name is-text is-semi-bold is-capitalize">{{ plan.name }}</h4>
      <div class="plan-card__icons is-text">
        <div class="plan-card__icon-wrapper">
          <gauge-icon class="plan-card__icon" :fill-percentage="plan.gaugeFill" />
          <div class="plan-card__icon-text is-text is-semi-bold">
            <div>{{ plan.speed.download }}{{ mbps ? 'Mbps' : '' }} down<sup>†</sup></div>
            <div>{{ plan.speed.upload }}{{ mbps ? 'Mbps' : '' }} up<sup>†</sup></div>
          </div>
        </div>
        <div v-if="plan.hyper && hyperCta" class="plan-card__icon-wrapper is-hyperfibre">
          <div class="plan-card__link is-text is-semi-bold">
            <ui-button
              tag="app-link"
              to="/hyperfibre/"
              class="plan-card__link-button is-narrow"
              :class="hyperCtaModifier ? `is-${hyperCtaModifier}` : ''"
            >
              <slot name="hyperCtaLabel">Find out more</slot>
            </ui-button>
          </div>
        </div>
        <div v-else class="plan-card__icon-wrapper">
          <household-icons class="plan-card__icon" :quantity="plan.householdSize" />
          <div class="plan-card__icon-text is-text is-semi-bold">
            <span>Household <br />size</span>
          </div>
        </div>
      </div>
      <div class="plan-card__footer">
        <ui-button expanded class="plan-card__button" :class="ctaModifier ? `is-${ctaModifier}` : ''" @click="onSelect">
          <slot name="ctaLabel">Select plan</slot>
        </ui-button>
      </div>
    </div>
    <popular-plan-icon v-if="displayPopularIcon && plan.isMostPopular" class="plan-card__popular-icon" />
  </div>
</template>

<script lang="ts" setup>
import { type PropType } from 'vue';
import { type Plan } from '@/types/products';
import { getFormattedDollars, getFormattedCents, getFormattedMoney } from '@/utils/money';
import UiButton from '@/components/global/ui/UiButton.vue';
import HouseholdIcons from '@/components/global/elements/HouseholdIcons.vue';
import GaugeIcon from '@/components/global/elements/GaugeIcon.vue';
import { BBC_PROMO_CODE, useMemberSignup } from '~/stores/signup';
import { computed } from '#imports';
import PopularPlanIcon from '~/components/global/elements/PopularPlanIcon.vue';

const emit = defineEmits(['selected']);

type PlanType = 'page' | 'signup';

const props = defineProps({
  plan: {
    type: Object as PropType<Plan>,
    default: () => ({}),
  },
  type: {
    type: String as PropType<PlanType>,
    default: 'page',
  },
  displayPopularIcon: {
    type: Boolean,
    default: false,
  },
  noTermDiscount: {
    type: Boolean,
    default: false,
  },
  ctaModifier: {
    type: String,
    default: '',
  },
  hyperCta: {
    type: Boolean,
    default: true,
  },
  hyperCtaModifier: {
    type: String,
    default: '',
  },
  mbps: {
    type: Boolean,
    default: true,
  },
  promo: {
    type: String,
    default: null,
  },
});

const signup = useMemberSignup();

const hasTerm = computed(() => !signup.signupNoTerm);

const hasNoTermDiscount = computed(() => !hasTerm.value && props.noTermDiscount);

const planPrice = computed(() => {
  if (hasPromo.value) {
    return hasPromoPrice.value ? props.plan.promo.bbc.price : props.plan.price;
  }

  if (hasNoTermDiscount.value && props.plan?.noTermDiscountPrice) {
    return props.plan.noTermDiscountPrice;
  }

  if (!hasTerm.value && props.plan?.noTermPrice) {
    return props.plan.noTermPrice;
  }
  if (signup.signupDetails.term?.signupPrice && props.plan?.signupPrice) {
    return props.plan.signupPrice;
  }
  return props.plan.price;
});

const hasPromo = computed(() => props.promo === BBC_PROMO_CODE);

const hasPromoPrice = computed(() => !!(hasPromo.value && props.plan?.promo?.bbc?.price && hasTerm.value));

const planOldPrice = computed(() => {
  if (hasPromo.value) {
    if (hasPromoPrice.value) {
      return props.plan.price;
    }
    return null;
  }

  if (hasNoTermDiscount.value && props.plan?.noTermPrice) {
    return props.plan.noTermPrice;
  }

  if (signup.signupDetails.term?.signupPrice && props.plan?.signupPrice) {
    return props.plan.price;
  }

  return null;
});

const offerHalfPrice = computed(() => signup.offerHalfPrice);

const freeDiscount = ref(false);

const onSelect = () => {
  emit('selected', props.plan);
};
</script>

<style lang="scss" scoped>
@use '@/assets/mixins/__index' as mix;

.plan-card {
  position: relative;

  &__inner {
    display: grid;
    grid-template-columns: max-content 1fr;
    padding-right: rem(15);
    overflow: hidden;
    border-radius: rem(30);
    gap: rem(10) rem(20);
    background: var(--dark-pink);
    color: var(--primary);

    @include mobile {
      gap: rem(10) rem(18);
    }
    @include tiny {
      gap: rem(10) rem(10);
    }

    @include desktop {
      gap: rem(20);
      padding-right: rem(40);
      grid-template-columns: max-content 1fr max-content;
    }
  }

  &__name {
    font-size: calc-clamp($mobile, $desktop, 19, 30);
    place-self: end start;

    @include touch {
      padding-top: rem(16);
    }

    @include desktop {
      font-size: rem(22);
      grid-column: span 2;
    }

    &.is-hyper {
      svg {
        width: rem(160);
        height: rem(30);
        @include mobile {
          width: rem(140);
          height: rem(30);
        }
      }
    }
  }

  &__price {
    font-weight: 500;
    display: grid;
    grid-auto-flow: column;
    place-content: center;
    place-items: baseline;
    place-self: center;
    line-height: 1;
    position: relative;

    &-wrapper {
      display: grid;
      background: var(--secondary);
      border-top-right-radius: rem(30);
      border-bottom-right-radius: rem(30);
      grid-row: span 3;
      @include mobile {
        padding: rem(15) rem(10);
        min-width: rem(180);
      }
      @include tiny {
        padding: rem(15) rem(5);
        min-width: rem(110);
      }
      @include tablet {
        min-width: rem(280);
        padding: rem(15) rem(20);
      }
      @include desktop {
        grid-row: span 2;
        min-width: rem(300);
        padding: rem(50) rem(20) rem(45);
      }
    }

    &-dollars {
      font-size: calc-clamp($tablet, $desktop, 24, 70);
      @include tiny {
        font-size: rem(20);
      }
    }

    &-old {
      position: absolute;
      right: 0;
      top: 0;
      font-size: calc-clamp($tablet, $desktop, 14, 18);
      transform: translateY(-90%);
      @include mobile {
        left: 50%;
        right: auto;
        transform: translate(-50%, -90%);
        top: rem(-10);
      }

      &:after {
        content: '';
        position: absolute;
        //background: var(--primary);
        background: var(--error);
        display: block;
        height: rem(2);
        border-radius: 90px;
        width: 100%;
        transform: skew(0, -10deg);
        bottom: rem(2);
        transform-origin: left bottom;
      }
    }

    &-cents,
    &-sup {
      top: rem(10);
    }

    &-sup {
      font-size: calc-clamp($tablet, $desktop, 12, 20);
      align-self: start;
      transform: translateY(40%);
      margin-right: 0.2em;
    }

    &-cents {
      font-size: calc-clamp($tablet, $desktop, 14, 35);
    }
  }

  &__details {
    font-size: calc-clamp($tablet, $desktop, 13, 14);
    position: absolute;
    bottom: 0;
    left: 0;
    transform: translateY(100%);
    font-weight: 400;
    @include mobile {
      text-align: center;
      bottom: rem(-10);
    }
    @include tablet {
      font-weight: 400;
      padding-left: rem(15);
    }
  }

  &__icons {
    display: grid;
    gap: rem(7);
    white-space: nowrap;
    place-self: start;
    width: 100%;

    @include desktop {
      grid-auto-columns: max-content;
      gap: rem(20);
      grid-auto-flow: column;
    }
  }

  &__icon-wrapper {
    display: grid;
    grid-template-columns: rem(70) max-content;
    align-self: center;
    justify-items: center;
    align-items: center;
    gap: rem(10);

    @include tiny {
      grid-template-columns: rem(56) max-content;
      gap: rem(8);
    }

    @include tablet {
      grid-template-columns: max-content max-content;
    }

    &.is-hyperfibre {
      grid-template-columns: 1fr;
      display: block;
      @include touch {
        margin-top: rem(10);
      }
    }
  }

  &__link {
    width: 100%;
    &-button {
      max-width: rem(240);
      width: 100%;
      min-width: 0;
    }
  }

  &__icon {
    &.is-tree {
      height: rem(35);
    }
  }

  &__icon-text {
    font-size: rem(14);
    line-height: 1.1;
    @include tiny {
      font-size: rem(12);
    }
  }

  &__button {
    width: auto;
    max-width: rem(240);
    @include tiny {
      padding-left: 1em;
      padding-right: 1em;
      min-width: 0;
    }

    &.is-red {
      background-color: var(--red);
      border-color: var(--red);
      @include hover {
        border-color: var(--red);
        color: var(--red);
      }
    }
  }

  &__footer {
    @include touch {
      padding-bottom: rem(15);
    }
  }

  &__popular-icon {
    position: absolute;
    top: rem(-40);
    right: rem(-50);
    width: rem(180);
    height: auto;

    @include mobile {
      top: rem(-20);
      right: rem(-25);
      width: rem(120);
    }

    @include tiny {
      right: unset;
      left: rem(-25);
      width: rem(100);
    }
  }
}
</style>
